import type { MinCopy } from '@/i18n/types'

export const minUk: MinCopy = {
  brand: {
    name: 'Lexio',
    tagline: 'Розумій англійську, поки читаєш',
    beta: 'Beta',
  },
  nav: {
    today: 'Сьогодні',
    catalog: 'Каталог',
    words: 'Словник',
    reading: 'Читання',
    refresh: 'Повторення',
    challenge: 'Перевірка',
    progress: 'Прогрес',
    practice: 'Практика',
    settings: 'Налаштування',
    feedback: 'Зворотний звʼязок',
    faq: 'FAQ',
    mobileWords: 'Слова',
    mobileReview: 'Повтор',
    more: 'Ще',
    logout: 'Вийти',
    mobileNavAria: 'Мобільна навігація',
    closeMenuAria: 'Закрити меню',
    close: 'Закрити',
  },
  auth: {
    localeSwitcherLabel: 'Мова інтерфейсу',
    loginTitle: 'Lexio',
    loginDescription: 'Увійди, щоб продовжити вивчення мови.',
    email: 'Email',
    password: 'Пароль',
    forgotPassword: 'Забули пароль?',
    signIn: 'Увійти',
    or: 'або',
    continueWithGoogle: 'Продовжити з Google',
    noAccount: 'Немає акаунта?',
    registerLink: 'Реєстрація',
    invalidEmail: 'Некоректний email',
    passwordRequired: 'Введи пароль',
    googleAuthFailed: 'Не вдалося увійти через Google. Спробуй email і пароль або зайди пізніше.',
    unableSignIn: 'Зараз не вдається увійти. Спробуй ще раз.',
    invalidCredentials: 'Невірний email або пароль',
    tooManyAttempts: 'Забагато спроб. Спробуй пізніше.',
    registerTitle: 'Створи акаунт Lexio',
    registerDescription: 'Словник, читання й повторення — в одному місці.',
    displayName: "Ім'я",
    confirmPassword: 'Підтверди пароль',
    createAccount: 'Створити акаунт',
    agreePrefix: 'Створюючи акаунт, ти приймаєш',
    termsOfService: 'Умови використання',
    agreeAnd: 'та',
    privacyPolicy: 'Політику конфіденційності',
    agreeSuffix: '.',
    haveAccount: 'Вже є акаунт?',
    signInLink: 'Увійти',
    passwordMin: 'Пароль — не менше 8 символів',
    passwordUpper: 'Потрібна хоча б 1 велика літера',
    passwordDigit: 'Потрібна хоча б 1 цифра',
    passwordsMismatch: 'Паролі не збігаються',
    unableRegister: 'Зараз не вдається створити акаунт. Спробуй ще раз.',
    emailAlreadyRegistered: 'Цей email уже зареєстровано',
    resetTitle: 'Скидання пароля',
    resetDescription: 'Вкажи email — ми надішлемо посилання для скидання.',
    sendResetLink: 'Надіслати посилання',
    backTo: 'Назад до',
    resetSent:
      'Якщо акаунт із таким email є, посилання скоро надійде. Перевір також папку «Спам».',
    resetTooMany: 'Забагато запитів із цього пристрою. Зачекай кілька хвилин.',
    resetUnable: 'Зараз не вдається надіслати посилання. Спробуй пізніше.',
    chooseNewPasswordTitle: 'Новий пароль',
    chooseNewPasswordDescription: 'Придумай надійний пароль, щоб знову увійти в акаунт.',
    newPassword: 'Новий пароль',
    confirmNewPassword: 'Підтверди новий пароль',
    saveNewPassword: 'Зберегти пароль',
    rememberedPassword: "Згадав пароль?",
    resetLinkInvalid: 'Посилання для скидання недійсне або застаріло.',
    resetUnableConfirm: 'Зараз не вдається скинути пароль. Спробуй пізніше.',
    passwordResetSuccess: 'Пароль оновлено. Увійди в акаунт.',
    checkEmailTitle: 'Перевір пошту',
    verifyingTitle: 'Підтверджуємо email',
    verifyingMessage: 'Підтверджуємо email…',
    verificationSent:
      'Ми надіслали посилання для підтвердження на твій email. Перевір також папку «Спам».',
    emailPlaceholder: 'you@example.com',
    sendVerificationLink: 'Надіслати посилання ще раз',
    alreadyVerified: 'Уже підтвердив?',
    verifyingHint: 'Не закривай сторінку, поки ми активуємо акаунт.',
    verificationLinkInvalid: 'Посилання недійсне або застаріло. Запроси нове нижче.',
    enterRegisteredEmail: 'Введи email, який використовував під час реєстрації.',
    verificationResent:
      'Якщо акаунт ще не підтверджено, нове посилання надіслано. Перевір також «Спам».',
    verificationTooMany: 'Забагато запитів. Спробуй пізніше.',
    verificationUnable: 'Зараз не вдається надіслати посилання. Спробуй ще раз.',
    signingInWithGoogle: 'Входимо через Google…',
  },
  onboarding: {
    wizardTitle: 'Ласкаво просимо до Lexio',
    wizardSubtitle: 'Розкажи трохи про себе — так ми підлаштуємо навчання.',
    stepUiTitle: 'Якою мовою показувати додаток?',
    stepUiDescription: 'Меню, кнопки та intro будуть цією мовою. Пізніше можна змінити в Налаштуваннях.',
    uiLanguageLabel: 'Мова інтерфейсу',
    stepNativeTitle: 'Яка мова навчання?',
    stepNativeDescription: 'Ми використовуємо її для перекладів, підказок у тексті та пояснень.',
    stepThemeTitle: 'Як має виглядати Lexio?',
    stepThemeDescription: 'Обери світлу або темну тему — пізніше можна змінити в Налаштуваннях.',
    stepLevelTitle: 'Який у тебе рівень англійської?',
    stepLevelDescription: 'Статті, повторення й практика будуть під цей рівень.',
    stepDurationTitle: 'Скільки часу хочеш займатися щодня?',
    stepDurationDescription: 'Щоденна сесія підлаштується під цей час.',
    stepDurationLabel: '{minutes} хв',
    stepTopicsTitle: 'Про що любиш читати?',
    stepTopicsDescription: 'Обери кілька тем — так ми рекомендуватимемо статті. Можна пропустити.',
    stepTopicsSkip: 'Пропустити',
    stepCompleteTitle: 'Готово!',
    stepCompleteDescription: 'Lexio підлаштує читання, словник і щоденні сесії під твої відповіді.',
    stepCompleteHint: 'Усе це можна змінити в Налаштуваннях у будь-який момент.',
    next: 'Далі',
    back: 'Назад',
    finish: 'Почати',
    languageLabel: 'Мова навчання',
    levelLabel: 'Рівень',
    saveFailed: 'Не вдалося зберегти налаштування. Спробуй ще раз.',
  },
  learningDemo: {
    skip: 'Пропустити intro',
    introTitle: 'Один день навчання',
    introPhilosophy:
      'Не потрібно заучувати тисячі слів. Читай, зберігай те, що важливо, — і Lexio допоможе згадати це вчасно.',
    introCta: 'Показати як',
    questStep: 'Крок {current} з {total}',
    readTitle: 'Читай',
    readBody: 'Прочитай короткий текст. Коли зустрінеш незнайоме слово — натисни на нього.',
    understandTitle: 'Розумій',
    understandBody: 'Ми пояснюємо слово в контексті. Якщо воно корисне — збережи до словника.',
    vocabularyTitle: 'Твій словник',
    vocabularyBody: 'Збережені слова живуть тут — це твій особистий список для вивчення.',
    refreshTitle: 'Повторюй',
    refreshBody: 'Пізніше сьогодні або завтра повернись до повторення. Інтервальні повторення допомагають словам закріпитися.',
    todayTitle: 'Сьогодні',
    todayBody: 'Твоя база на день — ми підкажемо, що читати й що повторити за прогресом.',
    tapWordHint: 'Натисни на підсвічене слово',
    saveWord: 'Зберегти слово',
    saving: 'Зберігаємо…',
    saved: 'Збережено!',
    savedWordLabel: 'Твоє перше слово',
    vocabularyStatusNew: 'Нове',
    continue: 'Далі',
    completeTitle: 'Ти готовий',
    completeBody: 'Словник уже почато. Відкрий першу статтю й тапни будь-яке незнайоме слово.',
    completeCta: 'Почати читати',
    replay: 'Пройти intro знову',
    replayDescription: 'Ще раз пройти коротке знайомство з додатком.',
    readLabel: 'Читай',
    understandLabel: 'Розумій',
    means: 'Значення',
    inContext: 'У цьому реченні',
    definitionLabel: 'Означення',
    exampleLabel: 'Приклад',
    vocabularyMoreHint:
      'У словнику також є прогрес, історія повторень і особисті нотатки — це більше, ніж простий список слів.',
    refreshFlipHint: 'Натисни на картку, щоб перевернути',
    refreshCardLabel: 'Картка повторення',
    refreshMoreHint:
      'У повторенні є різні режими, статистика сесії та інтервальний розклад — це лише швидкий погляд.',
    todayPreviewHint: 'Звичайний день виглядає так:',
    todayCardLabel: 'Твій день',
    todayStepReading: 'Продовжити читання',
    todayStepReadingHint: 'Візьми статтю свого рівня',
    todayStepNewWords: 'Зберігати нові слова',
    todayStepNewWordsHint: 'Натискай на слова під час читання, щоб поповнювати список',
    todayStepRefresh: 'Повторити слова',
    todayStepRefreshHint: 'Повторюй слова до строку, щоб вони закріпилися',
    todayMoreHint: '«Сьогодні» збирає читання, словник і повторення в одному місці.',
    completeHelpHint:
      'Питання? Відкрий FAQ у бічній панелі (на телефоні — в «Ще»). Ідея чи баг — натисни «Зворотний звʼязок» просто над FAQ.',
    translation: 'Переклад',
    playPronunciation: 'Вимова',
    demoArticleTitle: 'Перед школою',
    saveWordFailed: 'Не вдалося зберегти слово. Спробуй ще раз.',
    loadExplanationFailed: 'Не вдалося завантажити пояснення слова. Спробуй ще раз.',
    loadingExplanation: 'Завантажуємо пояснення…',
  },
  faq: {
    title: 'Часті запитання',
    intro: 'Коротко про те, як Lexio працює зараз — читання, збереження слів, повторення й акаунт.',
    feedbackTitle: 'Напиши, що думаєш',
    feedbackHint: 'Лайки, складнощі, баги, ідеї — ми читаємо кожне повідомлення.',
    breadcrumb: 'FAQ',
    items: [
      {
        id: 'what-is-lexio',
        question: 'Що таке Lexio?',
        answer:
          'Lexio допомагає розуміти англійську прямо під час читання. Натисни на слово чи фразу — отримаєш зрозуміле пояснення в контексті, збережи важливе й зустрінеш це знову через інтервальні повторення. Так ти продовжуєш читати, а не відкриваєш Google.',
      },
      {
        id: 'how-to-start',
        question: 'Як почати?',
        answer:
          'Створи безкоштовний акаунт, підтверди email, обери рідну мову й рівень, пройди коротке демо. Далі відкрий Читання, зберігай незнайомі слова й роби Повторення, коли картки готові.',
      },
      {
        id: 'tap-understand',
        question: 'Як працює «натиснув — зрозумів»?',
        answer:
          'У статті натисни на слово (або виділи коротку фразу). Lexio покаже значення для цього речення, повʼязані форми за потреби та інші значення, якщо слово багатозначне. Перед збереженням обери потрібне значення.',
      },
      {
        id: 'save-meaning',
        question: 'Що відбувається, коли я зберігаю слово?',
        answer:
          'Слово потрапляє до словника однією карткою. Якщо пізніше додаси інше значення — воно розширить ту саму картку, без дублів. Повторення йде за цією карткою — ти практикуєш те, що реально зберіг.',
      },
      {
        id: 'reviews',
        question: 'Як працюють повторення?',
        answer:
          'Lexio використовує інтервальні повторення (SM-2). Складні слова повертаються раніше, легкі — пізніше. Відкрий Повторення з екрана «Сьогодні» або з меню, коли є картки до строку — це звичка, яка закріплює читання.',
      },
      {
        id: 'email-verify',
        question: 'Навіщо підтверджувати email?',
        answer:
          'Підтвердження захищає акаунти від фейкових реєстрацій. Після реєстрації за email відкрий посилання з листа (перевір «Спам»). Вхід через Google уже підтверджений і цей крок пропускає.',
      },
      {
        id: 'forgot-password',
        question: 'Забув пароль. Що робити?',
        answer:
          'На сторінці входу обери «Забули пароль?», вкажи email і відкрий посилання з листа. Посилання діє годину. Якщо листа немає — перевір «Спам» і той самий email, що в акаунта.',
      },
      {
        id: 'wrong-translation',
        question: 'Що якщо переклад здається хибним?',
        answer:
          'Спочатку подивись «Інші значення» в підказці — сенс із контексту позначено як «У цьому реченні». Якщо все одно невірно — напиши через Налаштування → Зворотний звʼязок або форму на цій сторінці. Помилки допомагають покращувати Trust Ops.',
      },
      {
        id: 'mobile',
        question: 'Lexio працює на телефоні?',
        answer:
          'Так — це мобільний вебзастосунок. Можна додати на домашній екран. Нижнє меню: Сьогодні, Читання, Словник і Повторення. Контент прокручується над меню, дії лишаються під рукою.',
      },
      {
        id: 'free',
        question: 'Lexio безкоштовний?',
        answer:
          'Так для основного циклу: читання, розуміння за натисканням, словник і повторення. Частина AI-функцій може мати денні ліміти. Платні плани можуть зʼявитися пізніше; безкоштовний шлях лишиться основою щоденного розуміння англійської.',
      },
      {
        id: 'languages',
        question: 'Які мови підтримуються?',
        answer:
          'Ти читаєш англійську. Навчальні переклади — мовою навчання (російська, українська або польська). Інтерфейс також може бути англійською, російською, українською або польською — обери на лендінгу або збережи мову, обрану під час реєстрації.',
      },
    ],
  },
  feedback: {
    like: 'Мені подобається',
    dislike: 'Це збиває з пантелику',
    bug: 'Щось зламалося',
    idea: 'Ідея',
    placeholder: 'Що нам варто знати?',
    emailPlaceholder: 'Твій email',
    submit: 'Надіслати',
    done: 'Дякуємо — ми отримали повідомлення.',
    sendAnother: 'Ще повідомлення',
    error: 'Не вдалося надіслати. Спробуй трохи пізніше.',
    settingsTitle: 'Зворотний звʼязок',
    settingsHint: 'Напиши, що допомагає, що плутає або що зламалося.',
  },
  settings: {
    appearanceTitle: 'Оформлення',
    appearanceDescription: 'Тема і мова інтерфейсу.',
    uiLanguageLabel: 'Мова інтерфейсу',
    uiLanguageHint: 'Меню, кнопки й навігація. Окремо від мови навчання.',
    themeLabel: 'Тема',
    themeLight: 'Світла',
    themeDark: 'Темна',
    learningLanguageLabel: 'Мова навчання',
    learningLanguageHint: 'Для перекладів і пояснень твоєю мовою.',
  },
}
